import React, { useEffect, useState } from 'react';
import { socialPlatforms } from './schema';
import { getPlayerProfile } from '../../../../services/firestoreService';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'card',
  defaultHeadingSize: 'medium',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

/**
 * SocialLinksRenderer - Render component for social-links block
 * Now fetches links from profile data instead of block content
 * Platform colors are kept fixed (Twitter blue, YouTube red, etc.)
 * as they represent brand identity and should not be themed.
 */
const SocialLinksRenderer = ({ block, profileId, theme = {} }) => {
  const { content } = block;
  const {
    heading,
    alignment = 'center',
    size = 'medium',
    iconStyle,
    style: legacyStyle,
    visiblePlatforms = [],
  } = content;

  // Support renamed iconStyle field, falling back to legacy style field
  const iconDisplayStyle = iconStyle || (typeof legacyStyle === 'string' ? legacyStyle : 'filled');

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);

  useEffect(() => {
    const loadProfile = async () => {
      if (!profileId) {
        setLoading(false);
        return;
      }

      try {
        const fetchedProfile = await getPlayerProfile(profileId);
        setProfile(fetchedProfile);
      } catch (error) {
        console.error('Error loading profile:', error);
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [profileId]);

  // Get platform value from profile
  const getPlatformValue = (platform) => {
    if (!profile) return null;
    const parts = platform.profileField.split('.');
    let value = profile;
    for (const part of parts) {
      value = value?.[part];
      if (!value) return null;
    }
    return value;
  };

  // Build links array from profile data
  const links = socialPlatforms
    .filter((platform) => {
      const value = getPlatformValue(platform);
      if (!value) return false;
      // Check visibility
      if (visiblePlatforms.length > 0 && !visiblePlatforms.includes(platform.value)) {
        return false;
      }
      return true;
    })
    .map((platform) => ({
      id: platform.value,
      platform: platform.value,
      url: platform.urlTemplate(getPlatformValue(platform)),
      label: platform.label,
    }));

  const alignmentClasses = {
    left: 'justify-start',
    center: 'justify-center',
    right: 'justify-end',
  };

  const textAlignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const sizeClasses = {
    small: 'w-9 h-9 sm:w-10 sm:h-10',
    medium: 'w-10 h-10 sm:w-12 sm:h-12',
    large: 'w-12 h-12 sm:w-14 sm:h-14',
  };

  // FA icon size classes based on block size setting
  const faIconSizeClasses = {
    small: 'text-lg',
    medium: 'text-xl',
    large: 'text-2xl',
  };

  // Platform colors are kept fixed for brand recognition
  const getStyleClasses = (platform) => {
    const platformColors = {
      twitter: 'bg-[#000000]',
      youtube: 'bg-[#FF0000]',
      twitch: 'bg-[#9146FF]',
      instagram: 'bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF]',
      facebook: 'bg-[#1877F2]',
      discord: 'bg-[#5865F2]',
      linkedin: 'bg-[#0A66C2]',
      tiktok: 'bg-black',
      lichess: 'bg-white text-black',
      chesscom: 'bg-[#81B64C]',
      fide: 'bg-[#00457C]',
      github: 'bg-[#333]',
      website: '', // Uses accent color
    };

    if (iconDisplayStyle === 'filled') {
      if (platform === 'website') {
        return `text-white hover:opacity-80`;
      }
      return `${platformColors[platform] || 'bg-gray-700'} text-white hover:opacity-80`;
    }
    if (iconDisplayStyle === 'outline') {
      return 'bg-transparent border-2 transition-colors';
    }
    // minimal
    return 'bg-transparent transition-colors';
  };

  // Get inline styles for elements that need theme colors
  const getInlineStyles = (platform) => {
    if (iconDisplayStyle === 'filled' && platform === 'website') {
      return { backgroundColor: styles.accentColor };
    }
    if (iconDisplayStyle === 'outline') {
      return {
        borderColor: styles.mutedColor,
        color: styles.mutedColor,
      };
    }
    if (iconDisplayStyle === 'minimal') {
      return { color: styles.mutedColor };
    }
    return {};
  };

  // Platforms that use custom image icons
  const imagePlatforms = {
    chesscom: '/Images/Chesscom.png',
    lichess: '/Images/LiChess.png',
    fide: '/Images/FIDE.png',
  };

  // Get icon for platform using Font Awesome + custom images
  const getIcon = (platform) => {
    const iconClass = faIconSizeClasses[size] || 'text-xl';

    // Image-based platforms use images only in filled style; FA icons otherwise
    if (imagePlatforms[platform] && iconDisplayStyle === 'filled') {
      return (
        <img
          src={imagePlatforms[platform]}
          alt={platform}
          className="w-full h-full object-cover"
        />
      );
    }

    // FA icon mappings
    const faIcons = {
      youtube: `fab fa-youtube ${iconClass}`,
      twitter: `fab fa-x-twitter ${iconClass}`,
      facebook: `fab fa-facebook ${iconClass}`,
      instagram: `fab fa-instagram ${iconClass}`,
      tiktok: `fab fa-tiktok ${iconClass}`,
      twitch: `fab fa-twitch ${iconClass}`,
      discord: `fab fa-discord ${iconClass}`,
      linkedin: `fab fa-linkedin ${iconClass}`,
      github: `fab fa-github ${iconClass}`,
      website: `fas fa-globe ${iconClass}`,
      chesscom: `fas fa-chess-pawn ${iconClass}`,
      lichess: `fas fa-chess-knight ${iconClass}`,
      fide: `fas fa-chess ${iconClass}`,
    };

    const className = faIcons[platform] || `fas fa-globe ${iconClass}`;
    return <i className={className} />;
  };

  // Loading state
  if (loading) {
    return null;
  }

  // No links available
  if (links.length === 0) {
    return null;
  }

  return (
    <div
      className={`w-full ${styles.sectionPaddingClass}`}
      style={{ backgroundColor: styles.sectionBackground }}
    >
      <div className="max-w-4xl mx-auto">
        {/* Heading */}
        {heading && (
          <h3
            className={`${styles.headingSizeClass} font-semibold mb-4 sm:mb-6 ${textAlignClasses[alignment]}`}
            style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
          >
            {heading}
          </h3>
        )}

        {/* Social Icons */}
        <div className={`flex flex-wrap gap-3 sm:gap-4 ${alignmentClasses[alignment]}`}>
          {links.map((link) => (
            <a
              key={link.id}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`${sizeClasses[size]} ${getStyleClasses(link.platform)} rounded-full flex items-center justify-center min-h-[44px] min-w-[44px] overflow-hidden`}
              style={getInlineStyles(link.platform)}
              title={link.label}
              onMouseEnter={(e) => {
                if (iconDisplayStyle === 'outline' || iconDisplayStyle === 'minimal') {
                  e.currentTarget.style.color = styles.accentColor;
                  if (iconDisplayStyle === 'outline') {
                    e.currentTarget.style.borderColor = styles.accentColor;
                  }
                }
              }}
              onMouseLeave={(e) => {
                if (iconDisplayStyle === 'outline' || iconDisplayStyle === 'minimal') {
                  e.currentTarget.style.color = styles.mutedColor;
                  if (iconDisplayStyle === 'outline') {
                    e.currentTarget.style.borderColor = styles.mutedColor;
                  }
                }
              }}
            >
              {getIcon(link.platform)}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SocialLinksRenderer;
